// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type AcceptInviteBody = {
  token: string;
  password: string;
};
export const AcceptInviteBodySchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
}).strict();

export type AcceptInviteResult = {
  email: string;
};
export const AcceptInviteResultSchema = z.object({
  email: z.string(),
}).strict();

export type RegisterBody = {
  email: string;
  password: string;
  name: string;
  accountType?: "personal" | "organization" | undefined;
  orgName?: string | undefined;
  planSlug?: "free" | "pro" | undefined;
};
export const RegisterBodySchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, "password must be at least 8 characters").regex(new RegExp("\\d"), "password must contain at least 1 number").regex(new RegExp("[a-zA-Z]"), "password must contain at least 1 letter"),
  name: z.string().min(1),
  accountType: z.enum(["personal", "organization"] as const).optional(),
  orgName: z.string().min(1).max(200).optional(),
  planSlug: z.enum(["free", "pro"] as const).optional(),
}).strict().refine((b) => b.accountType !== 'organization' || !!b.orgName);

export type LoginBody = {
  email: string;
  password: string;
};
export const LoginBodySchema = z.object({
  email: z.string().min(1),
  password: z.string().min(1),
}).strict();

export type ForgotPasswordBody = {
  email: string;
};
export const ForgotPasswordBodySchema = z.object({
  email: z.string().email(),
}).strict();

export type ResetPasswordBody = {
  password: string;
};
export const ResetPasswordBodySchema = z.object({
  password: z.string().min(8, "password must be at least 8 characters").regex(new RegExp("\\d"), "password must contain at least 1 number").regex(new RegExp("[a-zA-Z]"), "password must contain at least 1 letter"),
}).strict();

export type ResetPasswordQuery = {
  token: string;
};
export const ResetPasswordQuerySchema = z.object({
  token: z.string().min(1),
}).strict();

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  platformRole?: string | undefined;
  organizationId?: string | null | undefined;
  lastActiveAt?: string | undefined;
  onboarding?: Record<string, unknown> | null | undefined;
};
export const AuthUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: z.string(),
  platformRole: z.string().optional(),
  organizationId: z.string().nullable().optional(),
  lastActiveAt: z.string().optional(),
  onboarding: z.record(z.unknown()).nullable().optional(),
}).strict();

export type AuthSession = {
  token: string;
  refreshToken: string;
  user: AuthUser;
  checkoutUrl?: string | null | undefined;
};
export const AuthSessionSchema = z.object({
  token: z.string(),
  refreshToken: z.string(),
  user: AuthUserSchema,
  checkoutUrl: z.string().nullable().optional(),
}).strict();

export type SignupResponse = AuthSession;

export async function signup(body: RegisterBody): Promise<SignupResponse> {
  const response = await api.post<SignupResponse>(`/auth/signup`, body);
  return response.data;
}

export type RegisterResponse = AuthSession;

export async function register(body: RegisterBody): Promise<RegisterResponse> {
  const response = await api.post<RegisterResponse>(`/auth/register`, body);
  return response.data;
}

export type LoginResponse = AuthSession;

export async function login(body: LoginBody): Promise<LoginResponse> {
  const response = await api.post<LoginResponse>(`/auth/login`, body);
  return response.data;
}

export type ForgotPasswordResponse = unknown;

export async function forgotPassword(body: ForgotPasswordBody): Promise<ForgotPasswordResponse> {
  const response = await api.post<ForgotPasswordResponse>(`/auth/forgot-password`, body);
  return response.data;
}

export type ResetPasswordResponse = unknown;

export async function resetPassword(body: ResetPasswordBody, query?: ResetPasswordQuery): Promise<ResetPasswordResponse> {
  const response = await api.post<ResetPasswordResponse>(`/auth/reset-password`, body, { params: query });
  return response.data;
}

export type AcceptInviteResponse = {
  data: AcceptInviteResult;
};

export async function acceptInvite(body: AcceptInviteBody): Promise<AcceptInviteResponse> {
  const response = await api.post<AcceptInviteResponse>(`/auth/accept-invite`, body);
  return response.data;
}

export const authKeys = {
  all: ["auth"] as const,
  lists: () => [...authKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...authKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...authKeys.all, 'detail'] as const,
  detail: (id: string) => [...authKeys.details(), id] as const,
};
